import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '../utils/cn';
import { ThemeContext, } from './theme-context';
const PREFERS_DARK = '(prefers-color-scheme: dark)';
function systemTheme() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return 'light';
    }
    return window.matchMedia(PREFERS_DARK).matches ? 'dark' : 'light';
}
/**
 * Wurzel-Provider fuer `@sectepe/ui`. Aktiviert die Design-System-Tokens ueber
 * die `.sec-scope`-Klasse (siehe @sectepe/design-system/components.css),
 * verwaltet Theme- und Sprach-Zustand und stellt beides ueber `useTheme()`
 * bereit. Setzt `data-sec-theme` und `lang` fuer nachgelagerte Komponenten und
 * Fremd-Apps (Guest-Bridge liest dieselben Attribute).
 *
 * Konsument muss zusaetzlich `@sectepe/design-system/index.css` einbinden,
 * damit die `--sec-*`-Tokens und `.sec-*`-Klassen verfuegbar sind.
 */
export function ThemeProvider({ children, defaultTheme = 'system', defaultLanguage = 'de', className, }) {
    const [theme, setTheme] = useState(defaultTheme);
    const [language, setLanguage] = useState(defaultLanguage);
    const [systemResolved, setSystemResolved] = useState(systemTheme);
    // Auf OS-Praeferenz reagieren, solange `theme === 'system'`.
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return;
        }
        const media = window.matchMedia(PREFERS_DARK);
        const onChange = () => setSystemResolved(media.matches ? 'dark' : 'light');
        onChange();
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, []);
    const resolvedTheme = theme === 'system' ? systemResolved : theme;
    const value = useMemo(() => ({
        theme,
        resolvedTheme,
        setTheme,
        language,
        setLanguage,
    }), [theme, resolvedTheme, language]);
    const setThemeStable = useCallback((next) => setTheme(next), []);
    const setLanguageStable = useCallback((next) => setLanguage(next), []);
    return (_jsx(ThemeContext.Provider, { value: { ...value, setTheme: setThemeStable, setLanguage: setLanguageStable }, children: _jsx("div", { className: cn('sec-scope', className), "data-sec-theme": resolvedTheme, lang: language, children: children }) }));
}
